import { useEffect, useState, useRef } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency, formatQty, unitLabel, API, whatsappInvoiceLink, downloadAuthedFile } from "../lib/api";
import { Plus, FileText, X, TrendingUp, TrendingDown, DollarSign, Download, MessageCircle, Trash2, Wallet, PenLine, Send, ScanLine, Search, Gem, RotateCcw, Edit3, Truck } from "lucide-react";
import QrScannerModal from "../components/QrScannerModal";
import { toast, Toaster } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAuth } from "../context/AuthContext";
import ResponsiveTable from "../components/ResponsiveTable";
import { ShareSignatureModal } from "./Memos";
import { EmeteraiApplyButton, SendPanel } from "../components/EmeteraiActions";

export default function Accounting() {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const showFinancials = !isStaff; // hide profit/expenses/chart for staff
  const showMargin = user?.role === "admin" || user?.role === "accountant";  // cost-sensitive
  const [tab, setTab] = useState("sales");
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [monthly, setMonthly] = useState({ series: [] });
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saleModal, setSaleModal] = useState(false);
  const [expenseModal, setExpenseModal] = useState(false);
  const [paymentSale, setPaymentSale] = useState(null);
  const [signShare, setSignShare] = useState(null);
  const [sendPanel, setSendPanel] = useState(null);
  const [returnSale, setReturnSale] = useState(null);
  const [editSale, setEditSale] = useState(null);

  const requestSignature = async (sale) => {
    try {
      const { data } = await api.post(`/accounting/sales/${sale.id}/share`);
      setSignShare({ ...data, sale });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mint link");
    }
  };

  const openSendPanel = async (sale) => {
    try {
      const { data } = await api.post(`/accounting/sales/${sale.id}/share`);
      setSendPanel({ doc: sale, shareToken: data.share_token });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open Send panel");
    }
  };

  const load = async () => {
    const tasks = [
      api.get("/accounting/sales"),
      api.get("/products"),
      api.get("/crm/customers"),
    ];
    if (showFinancials) {
      tasks.push(api.get("/accounting/expenses"));
      tasks.push(api.get("/accounting/monthly"));
    }
    const results = await Promise.all(tasks);
    setSales(results[0].data);
    setProducts(results[1].data.filter((x) => x.status !== "Sold" && x.quantity > 0));
    setCustomers(results[2].data);
    if (showFinancials) {
      setExpenses(results[3].data);
      setMonthly(results[4].data);
    }
  };

  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";

  const deleteSale = async (sale) => {
    const isLocked = sale.signed || sale.locked;
    const msg = isLocked
      ? `⚠ INVOICE ${sale.invoice_number} IS SIGNED${sale.emeterai_applied ? " + e-METERAI STAMPED" : ""}.\n\nDeleting will permanently erase the legal record AND restore stock.\n\nThis action is audit-logged. Continue?`
      : `Permanently delete invoice ${sale.invoice_number}? Stock will be restored.`;
    if (!window.confirm(msg)) return;
    try {
      const qs = isLocked ? "?force=true" : "";
      await api.delete(`/accounting/sales/${sale.id}${qs}`);
      toast.success(`Invoice ${sale.invoice_number} deleted${isLocked ? " (force override)" : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const deleteExpense = async (exp) => {
    if (!window.confirm("Permanently delete this expense?")) return;
    try {
      await api.delete(`/accounting/expenses/${exp.id}`);
      toast.success("Expense deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const totalRevenue = sales.reduce((a, b) => a + (b.total || 0), 0);
  const totalProfit = sales.reduce((a, b) => a + (b.profit || 0), 0);
  const totalExpenses = expenses.reduce((a, b) => a + (b.amount || 0), 0);

  const downloadInvoice = async (sale) => {
    try {
      await downloadAuthedFile(`/accounting/invoice/${sale.id}`, `${sale.invoice_number}.pdf`);
    } catch (err) {
      toast.error(err.message || "Could not download invoice");
    }
  };

  // Shipping label — 100×100 mm thermal PDF. Fetched auth'd + opened
  // inline in a new tab so the browser's PDF viewer handles Ctrl+P.
  const printShippingLabel = async (sale) => {
    try {
      const token = localStorage.getItem("gv_token");
      const res = await fetch(`${API}/accounting/sales/${sale.id}/shipping-label.pdf`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.message || "Could not open shipping label");
    }
  };

  const exportCSV = async (kind) => {
    try {
      await downloadAuthedFile(`/accounting/export/${kind}.csv`, `opikjewels-${kind}.csv`);
      toast.success(`${kind === "sales" ? "Sales" : "Expenses"} exported`);
    } catch (err) {
      toast.error(err.message || "Could not export CSV");
    }
  };

  return (
    <Layout title="Accounting" subtitle="Financial command">
      <Toaster theme="dark" position="top-right" />

      {/* Stats — hidden for staff (no cost/profit/expenses access) */}
      {showFinancials && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <div className="glass-card p-7">
          <div className="overline flex items-center gap-2"><TrendingUp className="w-3 h-3" /> Revenue</div>
          <div className="serif text-3xl mt-3" data-testid="stat-revenue">{formatCurrency(totalRevenue)}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">{sales.length} sales recorded</div>
        </div>
        <div className="glass-card p-7">
          <div className="overline flex items-center gap-2"><DollarSign className="w-3 h-3" /> Gross Profit</div>
          <div className="serif text-3xl mt-3 text-[#7BA585]" data-testid="stat-profit">{formatCurrency(totalProfit)}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">Margin {totalRevenue ? Math.round((totalProfit / totalRevenue) * 100) : 0}%</div>
        </div>
        <div className="glass-card p-7">
          <div className="overline flex items-center gap-2"><TrendingDown className="w-3 h-3" /> Expenses</div>
          <div className="serif text-3xl mt-3 text-[#C99356]" data-testid="stat-expenses">{formatCurrency(totalExpenses)}</div>
          <div className="mono text-xs text-[#8A8A8A] mt-2">{expenses.length} entries</div>
        </div>
      </div>
      )}

      {/* Monthly bar chart */}
      {showFinancials && (
      <div className="glass-card p-7 lg:p-10 mb-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="overline">Last 6 Months</div>
            <h2 className="serif text-2xl mt-1">Financial Performance</h2>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={monthly.series}>
              <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="month" stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#8A8A8A" }} />
              <Bar dataKey="revenue" fill="#D4AF37" />
              <Bar dataKey="expenses" fill="#C99356" />
              <Bar dataKey="profit" fill="#7BA585" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      {/* Tabs */}
      <div className="flex items-center flex-wrap gap-3 mb-6 border-b border-white/5">
        <TabButton active={tab === "sales"} onClick={() => setTab("sales")} testid="tab-sales">Sales</TabButton>
        {showFinancials && (
          <TabButton active={tab === "expenses"} onClick={() => setTab("expenses")} testid="tab-expenses">Expenses</TabButton>
        )}
        {showMargin && (
          <TabButton active={tab === "margin"} onClick={() => setTab("margin")} testid="tab-margin">Margin Report</TabButton>
        )}
        <div className="flex-1" />
        {tab === "sales" && (
          <>
            {showFinancials && (
              <button onClick={() => exportCSV("sales")} data-testid="export-sales-btn" className="btn-outline-gold px-4 py-2.5 inline-flex items-center gap-2">
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            )}
            <button onClick={() => setSaleModal(true)} data-testid="add-sale-btn" className="btn-gold px-5 py-2.5 inline-flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> Record Sale
            </button>
          </>
        )}
        {tab === "expenses" && showFinancials && (
          <>
            <button onClick={() => exportCSV("expenses")} data-testid="export-expenses-btn" className="btn-outline-gold px-4 py-2.5 inline-flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={() => setExpenseModal(true)} data-testid="add-expense-btn" className="btn-gold px-5 py-2.5 inline-flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> Add Expense
            </button>
          </>
        )}
      </div>

      {tab === "sales" && (
        <SalesTable
          sales={sales}
          showFinancials={showFinancials}
          onDownloadInvoice={downloadInvoice}
          isAdmin={isAdmin}
          onDeleteSale={deleteSale}
          onOpenPayments={(s) => setPaymentSale(s)}
          onRequestSignature={requestSignature}
          onOpenSend={openSendPanel}
          onOpenReturn={(s) => setReturnSale(s)}
          onOpenEdit={(s) => setEditSale(s)}
          onPrintShipping={printShippingLabel}
          onReload={load}
        />
      )}

      {tab === "expenses" && showFinancials && (
        <ExpensesTable expenses={expenses} isAdmin={isAdmin} onDeleteExpense={deleteExpense} />
      )}

      {tab === "margin" && showMargin && (
        <MarginReportTab />
      )}

      {saleModal && (
        <SaleModal
          products={products}
          customers={customers}
          onClose={() => setSaleModal(false)}
          onSaved={() => { setSaleModal(false); load(); }}
        />
      )}
      {expenseModal && (
        <ExpenseModal
          onClose={() => setExpenseModal(false)}
          onSaved={() => { setExpenseModal(false); load(); }}
        />
      )}
      {paymentSale && (
        <PaymentLedgerModal
          sale={paymentSale}
          isAdmin={isAdmin}
          onClose={() => setPaymentSale(null)}
          onChanged={(updated) => {
            setSales((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
            setPaymentSale(updated);
          }}
          onFullPaid={() => load()}
        />
      )}
      {signShare && (
        <ShareSignatureModal
          kind="sale"
          info={signShare}
          onClose={() => {
            setSignShare(null);
            load();
          }}
        />
      )}
      {sendPanel && (
        <SendPanel
          kind="sale"
          doc={sendPanel.doc}
          shareToken={sendPanel.shareToken}
          onClose={() => setSendPanel(null)}
        />
      )}
      {returnSale && (
        <ReturnSaleModal
          sale={returnSale}
          onClose={() => setReturnSale(null)}
          onSaved={() => { setReturnSale(null); load(); }}
        />
      )}
      {editSale && (
        <EditSaleModal
          sale={editSale}
          onClose={() => setEditSale(null)}
          onSaved={() => { setEditSale(null); load(); }}
        />
      )}
    </Layout>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 text-xs uppercase tracking-[0.18em] transition-colors relative ${
        active ? "text-[#D4AF37]" : "text-[#8A8A8A] hover:text-[#EAEAEA]"
      }`}
    >
      {children}
      {active && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#D4AF37]" />}
    </button>
  );
}

function SalesTable({ sales, showFinancials, onDownloadInvoice, isAdmin, onDeleteSale, onOpenPayments, onRequestSignature, onOpenSend, onOpenReturn, onOpenEdit, onPrintShipping, onReload }) {
  const pieceCell = (s) => {
    if (s.is_multi && s.items_count > 1) {
      return (
        <div className="min-w-0">
          <div className="truncate">{s.items_count} items</div>
          <div className="text-[0.65rem] text-[#666] mono mt-0.5 truncate" title={s.items?.map((it) => it.product_name).join(", ")}>
            {s.items?.slice(0, 2).map((it) => it.product_name).join(", ")}
            {s.items_count > 2 ? ` +${s.items_count - 2}` : ""}
          </div>
        </div>
      );
    }
    return (
      <div className="min-w-0">
        <div className="truncate">{s.product_name}</div>
        <div className="text-[0.65rem] text-[#666] mono mt-0.5">
          {formatQty(s.quantity, s.product_unit, s.product_custom_unit)}
        </div>
      </div>
    );
  };

  const columns = [
    { key: "invoice", label: "Invoice", width: "120px", cell: (s) => <span className="mono text-xs text-[#D4AF37]">{s.invoice_number}</span> },
    { key: "date", label: "Date", width: "110px", cell: (s) => <span className="mono text-xs text-[#8A8A8A]">{s.created_at?.slice(0, 10)}</span>, hideOnMobile: true },
    { key: "piece", label: "Piece", width: "30%", cell: pieceCell },
    { key: "customer", label: "Customer", cell: (s) => (
      <div className="min-w-0">
        <div className="text-[#8A8A8A] truncate">{s.customer_name}</div>
        {s.sold_by_name && (
          <div className="text-[0.65rem] text-[#666] mono mt-0.5 truncate" title={`Sold by ${s.sold_by_name}`}>
            by {s.sold_by_name}
          </div>
        )}
      </div>
    ) },
    { key: "total", label: "Total", align: "right", width: "140px", cell: (s) => <span className="mono">{formatCurrency(s.total)}</span> },
    { key: "pay_status", label: "Payment", align: "center", width: "130px", cell: (s) => <PaymentStatusBadge sale={s} /> },
    ...(showFinancials
      ? [{
          key: "profit",
          label: "Profit · Margin",
          align: "right",
          width: "150px",
          cell: (s) => {
            const profit = Number(s.profit ?? 0);
            const total = Number(s.total ?? 0);
            const pct = total > 0 ? (profit / total) * 100 : 0;
            const isLoss = profit < 0;
            return (
              <span className={`mono ${isLoss ? "text-red-400" : "text-[#7BA585]"}`}>
                {formatCurrency(profit)}
                <span className="text-[0.65rem] text-[#666] ml-1.5">· {pct.toFixed(1)}%</span>
              </span>
            );
          },
          hideOnMobile: true,
        }]
      : []),
  ];

  const rowActions = (s) => (
    <>
      {whatsappInvoiceLink(s) && (
        <a
          href={whatsappInvoiceLink(s)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`whatsapp-${s.invoice_number}`}
          className="min-h-[44px] sm:min-h-0 text-[#7BA585] hover:text-[#9DC9A9] text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2"
          title={`Send to ${s.customer_phone}`}
        >
          <MessageCircle className="w-3 h-3" /> WhatsApp
        </a>
      )}
      <button
        onClick={() => onDownloadInvoice(s)}
        data-testid={`invoice-${s.invoice_number}`}
        className="min-h-[44px] sm:min-h-0 text-[#D4AF37] hover:text-[#F3E5AB] text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2"
      >
        <FileText className="w-3 h-3" /> Invoice
      </button>
      <button
        onClick={() => onOpenPayments(s)}
        data-testid={`payments-${s.invoice_number}`}
        className={`min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 ${
          s.payment_status === "Paid" ? "text-[#7BA585] hover:text-[#9DC9A9]" : "text-[#C99356] hover:text-[#E5B481]"
        }`}
        title={s.payment_status === "Paid" ? "View payment history" : `Balance Rp ${(s.balance_due || 0).toLocaleString()}`}
      >
        <Wallet className="w-3 h-3" /> Payments
      </button>
      <button
        onClick={() => onRequestSignature(s)}
        data-testid={`sign-link-${s.invoice_number}`}
        className={`min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 ${
          s.signed ? "text-[#7BA585]" : "text-[#D4AF37] hover:text-[#F3E5AB]"
        }`}
        title={s.signed ? `Signed by ${s.signer_name || "customer"}` : "Get customer signature link"}
      >
        <PenLine className="w-3 h-3" /> {s.signed ? "Signed" : "Sign Link"}
      </button>
      {s.signed && (
        <EmeteraiApplyButton
          kind="sale"
          doc={s}
          testidPrefix={s.invoice_number}
          onApplied={() => onReload?.()}
        />
      )}
      {s.signed && (
        <button
          onClick={() => onOpenSend(s)}
          data-testid={`send-${s.invoice_number}`}
          className="min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 text-[#7BA585] hover:text-[#9DC9A9]"
          title="Send official PDF to customer"
        >
          <Send className="w-3 h-3" /> Send
        </button>
      )}
      {isAdmin && (
        <button
          onClick={() => onOpenEdit?.(s)}
          data-testid={`edit-sale-${s.invoice_number}`}
          className="min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 text-[#8A8A8A] hover:text-[#D4AF37]"
          title="Edit customer info or transaction date"
        >
          <Edit3 className="w-3 h-3" /> Edit
        </button>
      )}
      {isAdmin && (
        <button
          onClick={() => onOpenReturn?.(s)}
          data-testid={`return-sale-${s.invoice_number}`}
          className="min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 text-[#C99356] hover:text-[#E5B481]"
          title="Process customer return"
        >
          <RotateCcw className="w-3 h-3" /> Return
        </button>
      )}
      <button
        onClick={() => onPrintShipping?.(s)}
        data-testid={`shipping-${s.invoice_number}`}
        className="min-h-[44px] sm:min-h-0 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 px-2 py-2 text-[#8A8A8A] hover:text-[#D4AF37]"
        title="Print shipping label"
      >
        <Truck className="w-3 h-3" /> Shipping
      </button>
      {isAdmin && (
        <button
          onClick={() => onDeleteSale(s)}
          data-testid={`delete-sale-${s.invoice_number}`}
          className={`min-h-[44px] sm:min-h-0 p-2 inline-flex items-center justify-center ${
            s.signed || s.locked
              ? "text-[#C99356] hover:text-red-400"
              : "text-[#8A8A8A] hover:text-red-400"
          }`}
          title={s.signed || s.locked ? "Force delete — signed/locked (admin override)" : "Delete invoice"}
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.25} />
        </button>
      )}
    </>
  );

  return (
    <ResponsiveTable
      columns={columns}
      rows={sales}
      rowKey={(s) => s.id}
      testid="sales-table"
      rowActions={rowActions}
      emptyMessage="No sales recorded."
      mobileCard={(s) => ({
        title: <span className="text-[#D4AF37] mono text-xs">{s.invoice_number}</span>,
        subtitle: <span>{s.customer_name} · {s.created_at?.slice(0, 10)}</span>,
        fields: [
          { label: "Total", value: <span className="mono">{formatCurrency(s.total)}</span> },
          { label: "Status", value: <PaymentStatusBadge sale={s} /> },
          { label: "Piece", value: pieceCell(s) },
          ...(showFinancials ? [{
            label: "Profit · Margin",
            value: (() => {
              const profit = Number(s.profit ?? 0);
              const total = Number(s.total ?? 0);
              const pct = total > 0 ? (profit / total) * 100 : 0;
              const isLoss = profit < 0;
              return (
                <span className={`mono ${isLoss ? "text-red-400" : "text-[#7BA585]"}`}>
                  {formatCurrency(profit)} · {pct.toFixed(1)}%
                </span>
              );
            })(),
          }] : []),
          { label: "Payment Method", value: <span>{s.payment_method || "—"}</span> },
        ],
        actions: rowActions(s),
      })}
    />
  );
}

/**
 * MarginReportTab — Admin/Accountant only. Per-category COGS + gross margin
 * breakdown over the chosen period (month / quarter / YTD / all-time). Fetches
 * /accounting/margin-report. Heavy in revenue/COGS rows is sorted by gross
 * profit descending so the most profitable categories surface first.
 */
function MarginReportTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/accounting/margin-report?period=${period}`)
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err.response?.data?.detail || "Could not load margin report"))
      .finally(() => setLoading(false));
  }, [period]);

  const labels = { month: "This Month", quarter: "This Quarter", ytd: "Year-to-Date", all: "All Time" };

  return (
    <div data-testid="margin-report">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {(["month", "quarter", "ytd", "all"]).map((p) => (
          <button
            key={p}
            data-testid={`margin-period-${p}`}
            onClick={() => setPeriod(p)}
            className={`px-4 py-2 text-[0.65rem] uppercase tracking-wider border transition-all ${
              period === p ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#8A8A8A]"
            }`}
          >
            {labels[p]}
          </button>
        ))}
      </div>

      {loading && !data ? (
        <div className="text-[#8A8A8A] text-sm py-10 text-center">Loading…</div>
      ) : !data ? null : (
        <>
          {/* Totals card */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-7" data-testid="margin-totals">
            <SummaryTile label="Revenue" value={formatCurrency(data.totals.revenue)} />
            <SummaryTile label="COGS" value={formatCurrency(data.totals.cogs)} muted />
            <SummaryTile
              label="Gross Profit"
              value={formatCurrency(data.totals.gross_profit)}
              accent={data.totals.gross_profit >= 0}
              danger={data.totals.gross_profit < 0}
            />
            <SummaryTile
              label="Gross Margin"
              value={`${data.totals.gross_margin_pct.toFixed(2)}%`}
              accent={data.totals.gross_margin_pct >= 30}
              danger={data.totals.gross_margin_pct < 0}
              sub={`${data.totals.sales_count} invoices`}
            />
          </div>

          {/* Per-category table */}
          {data.categories.length === 0 ? (
            <div className="border border-white/5 px-5 py-10 text-center text-[#8A8A8A] text-sm">
              No sales recorded in this period.
            </div>
          ) : (
            <div className="overflow-x-auto border border-white/5">
              <table className="w-full text-xs" data-testid="margin-table">
                <thead>
                  <tr className="text-left overline text-[0.55rem] text-[#7a7a7a] border-b border-white/5">
                    <th className="px-4 py-3 font-normal">Category</th>
                    <th className="px-4 py-3 font-normal text-right">Revenue</th>
                    <th className="px-4 py-3 font-normal text-right">COGS</th>
                    <th className="px-4 py-3 font-normal text-right">Gross Profit</th>
                    <th className="px-4 py-3 font-normal text-right">Margin %</th>
                    <th className="px-4 py-3 font-normal text-right">Units</th>
                    <th className="px-4 py-3 font-normal text-right">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {data.categories.map((c) => {
                    const isLoss = c.gross_profit < 0;
                    return (
                      <tr key={c.category} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`margin-row-${c.category}`}>
                        <td className="px-4 py-3 text-[#EAEAEA]">{c.category}</td>
                        <td className="px-4 py-3 mono text-right">{formatCurrency(c.revenue)}</td>
                        <td className="px-4 py-3 mono text-right text-[#8A8A8A]">{formatCurrency(c.cogs)}</td>
                        <td className={`px-4 py-3 mono text-right ${isLoss ? "text-red-400" : "text-[#7BA585]"}`}>
                          {formatCurrency(c.gross_profit)}
                        </td>
                        <td className={`px-4 py-3 mono text-right ${isLoss ? "text-red-400" : c.gross_margin_pct >= 30 ? "text-[#D4AF37]" : "text-[#EAEAEA]"}`}>
                          {c.gross_margin_pct.toFixed(2)}%
                        </td>
                        <td className="px-4 py-3 mono text-right text-[#8A8A8A]">{formatQty(c.units_sold, "pcs")}</td>
                        <td className="px-4 py-3 mono text-right text-[#8A8A8A]">{c.sales_count}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 text-[0.65rem] text-[#666] leading-relaxed">
            Revenue is the sum of line subtotals (before invoice-level discount & tax).
            COGS uses each piece's current `cost_price`. Category is read from each product live.
          </div>
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value, sub, accent, danger, muted }) {
  return (
    <div className={`border p-4 ${danger ? "border-red-400/40 bg-red-400/[0.04]" : accent ? "border-[#D4AF37]/30 bg-[#D4AF37]/[0.04]" : "border-white/5 bg-white/[0.02]"}`}>
      <div className="overline text-[0.55rem] text-[#7a7a7a]">{label}</div>
      <div className={`serif text-xl mt-1 ${danger ? "text-red-400" : accent ? "text-[#D4AF37]" : muted ? "text-[#8A8A8A]" : "text-[#EAEAEA]"}`}>
        {value}
      </div>
      {sub && <div className="text-[0.65rem] text-[#666] mt-1">{sub}</div>}
    </div>
  );
}


function ExpensesTable({ expenses, isAdmin, onDeleteExpense }) {
  const columns = [
    { key: "date", label: "Date", width: "120px", cell: (e) => <span className="mono text-xs text-[#8A8A8A]">{(e.date || e.created_at)?.slice(0, 10)}</span> },
    { key: "category", label: "Category", width: "160px", cell: (e) => <span>{e.category}</span> },
    { key: "description", label: "Description", cell: (e) => <span className="text-[#8A8A8A] truncate">{e.description}</span> },
    { key: "amount", label: "Amount", align: "right", width: "140px", cell: (e) => <span className="mono text-[#C99356]">{formatCurrency(e.amount)}</span> },
  ];
  const rowActions = isAdmin
    ? (e) => (
      <button
        onClick={() => onDeleteExpense(e)}
        data-testid={`delete-expense-${e.id}`}
        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 inline-flex items-center justify-center"
        aria-label="Delete expense"
      >
        <Trash2 className="w-4 h-4" strokeWidth={1.25} />
      </button>
    )
    : undefined;
  return (
    <ResponsiveTable
      columns={columns}
      rows={expenses}
      rowKey={(e) => e.id}
      testid="expenses-table"
      emptyMessage="No expenses recorded."
      rowActions={rowActions}
      mobileCard={(e) => ({
        title: <span>{e.category}</span>,
        subtitle: <span>{(e.date || e.created_at)?.slice(0, 10)}</span>,
        fields: [
          { label: "Amount", value: <span className="mono text-[#C99356]">{formatCurrency(e.amount)}</span> },
          { label: "Description", value: <span>{e.description || "—"}</span> },
        ],
        actions: rowActions ? rowActions(e) : null,
      })}
    />
  );
}

// ---------- POS-STYLE MULTI-PRODUCT SALE MODAL ----------
const blankLine = () => ({
  key: Math.random().toString(36).slice(2),
  product_id: "",
  productInput: "",
  variant_id: "",
  quantity: 1,
  unit_price: 0,
});

/** Smooth thumbnail-based product picker — mirrors the POS catalog search.
 *  Opens as a centered sheet over the Sale modal. Search is debounced via
 *  React's intrinsic batching (instant client-side filter, no native iOS
 *  autocomplete bar). Supports auto-creating a custom product when the
 *  typed name does not exist in the catalog. */
function ProductPickerSheet({ products, onPick, onClose }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    // Slight delay so iOS doesn't fight us for focus while the modal animates in
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const results = q.length === 0
    ? products.slice(0, 24)
    : products
        .filter((p) => {
          const hay = `${p.sku || ""} ${p.name || ""} ${p.category || ""}`.toLowerCase();
          return hay.includes(q);
        })
        .slice(0, 60);

  const exactMatchExists = q.length > 0 && results.some((p) =>
    (p.name || "").toLowerCase() === q || (p.sku || "").toLowerCase() === q
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center bg-black/85 p-3 sm:p-8 backdrop-blur-sm"
      data-testid="product-picker-sheet"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="overline text-[#D4AF37] text-[0.6rem]">Pick Product</div>
            <div className="text-[0.65rem] text-[#666] mt-0.5">{products.length} items in catalog</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#8A8A8A] hover:text-[#D4AF37] p-1" data-testid="picker-close" aria-label="Close picker">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-white/5">
          <div className="relative">
            <Search className="w-4 h-4 text-[#666] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search SKU, name or category…"
              className="luxe-input w-full pl-10 pr-3 py-2.5 text-sm"
              data-testid="picker-search-input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="picker-results">
          {results.length === 0 ? (
            <div className="text-center text-[#666] text-xs py-8 px-4">
              {q.length === 0 ? "Catalog is empty." : `No products match "${query}".`}
            </div>
          ) : (
            results.map((p) => {
              const stock = p.stock != null ? p.stock : (p.quantity || 0);
              const oos = stock <= 0;
              const thumb = Array.isArray(p.photos) && p.photos.length > 0 ? p.photos[0] : null;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => onPick({
                    productInput: `${p.sku} · ${p.name}`,
                    product_id: p.id,
                    variant_id: "",
                    unit_price: p.selling_price || 0,
                  })}
                  data-testid={`picker-pick-${p.sku}`}
                  className={`w-full text-left px-3 py-2.5 border-b border-white/5 flex items-start gap-3 transition-colors ${
                    oos
                      ? "bg-red-400/[0.03] hover:bg-red-400/[0.08]"
                      : "hover:bg-[#D4AF37]/[0.06]"
                  }`}
                >
                  <div className="w-12 h-12 shrink-0 border border-white/10 bg-white/[0.03] flex items-center justify-center overflow-hidden">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fb = e.currentTarget.parentElement.querySelector(".pkr-fb");
                          if (fb) fb.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className="pkr-fb w-full h-full items-center justify-center text-[#444]"
                      style={{ display: thumb ? "none" : "flex" }}
                    >
                      <Gem className="w-4 h-4" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#EAEAEA] truncate">{p.name}</div>
                    <div className="text-[0.65rem] mono text-[#666] mt-0.5">
                      {p.sku} · {p.category || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono text-xs text-[#D4AF37]">{formatCurrency(p.selling_price || 0)}</div>
                    <div className={`text-[0.6rem] mono mt-0.5 ${oos ? "text-red-400" : "text-[#8A8A8A]"}`}>
                      {oos ? "OUT OF STOCK" : `${stock} ${p.unit || "pcs"} avail`}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
        {q.length > 0 && !exactMatchExists && (
          <div className="px-3 py-2 border-t border-white/10 bg-[#7BA585]/[0.04]">
            <button
              type="button"
              onClick={() => onPick({
                productInput: query.trim(),
                product_id: "",
                variant_id: "",
                unit_price: 0,
              })}
              className="w-full text-[0.7rem] text-[#7BA585] hover:text-[#D4AF37] py-1.5 mono"
              data-testid="picker-add-custom"
            >
              + Use as custom product: "{query.trim()}"
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SaleModal({ products, customers, onClose, onSaved }) {
  const { user } = useAuth();
  const showMargin = user?.role === "admin" || user?.role === "accountant";
  const isAdmin = user?.role === "admin";
  const [customer_id, setCid] = useState("");
  const [customerInput, setCustomerInput] = useState("");
  const [customCustomerPhone, setCustomCustomerPhone] = useState("");
  const [customCustomerAddress, setCustomCustomerAddress] = useState("");
  const [items, setItems] = useState([blankLine()]);
  const [invoice_discount, setInvoiceDiscount] = useState(0);
  const [tax_pct, setTaxPct] = useState(0);
  const [payment_method, setPay] = useState("Wire");
  const [notes, setNotes] = useState("");
  // Partial-payment: amount received now (defaults to full grand_total below)
  const [amount_received, setAmountReceived] = useState("");
  const [deposit_type, setDepositType] = useState("Deposit");
  // Sales user / commission
  const [seller_id, setSellerId] = useState("");
  const [salesUsers, setSalesUsers] = useState([]);
  const [override_rate, setOverrideRate] = useState("");
  const [bonus, setBonus] = useState("");
  // Sold By — admin can attribute the sale to any active team member (tracking only)
  const [sold_by_user_id, setSoldBy] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // POS scanner — keeps modal open and adds scanned items as new lines.
  const [showScanner, setShowScanner] = useState(false);
  // Product picker sheet: null = closed, "new" = create new line, idx = update line idx
  const [pickerFor, setPickerFor] = useState(null);

  useEffect(() => {
    api.get("/sales-users?status=Active").then(({ data }) => setSalesUsers(data)).catch(() => {});
    if (isAdmin) {
      api.get("/auth/users")
        .then(({ data }) => setTeamMembers((data || []).filter((u) => u.is_active !== false)))
        .catch(() => {});
    }
  }, [isAdmin]);

  const matchedCustomer = customers.find((c) => c.name === customerInput);
  useEffect(() => {
    setCid(matchedCustomer ? matchedCustomer.id : "");
    // eslint-disable-next-line
  }, [customerInput]);
  const isCustomCustomer = !customer_id && customerInput.trim().length > 0;

  // Per-line helpers
  const updateLine = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeLine = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  /** Called by the POS QR scanner when a code is decoded. Adds the product
   *  as a new line item (qty=1, unit_price from product). If the user has an
   *  empty line, fills that instead so we don't litter the modal. */
  const handleScannedProduct = (_shortId, prod) => {
    const lineDefaults = {
      productInput: `${prod.sku} · ${prod.name}`,
      product_id: prod.id,
      variant_id: "",
      quantity: 1,
      unit_price: Number(prod.selling_price || 0),
      discount: 0,
      notes: "",
    };
    setItems((prev) => {
      // Reuse the first blank line if there is one
      const blankIdx = prev.findIndex((it) => !it.productInput && !it.product_id);
      if (blankIdx >= 0) {
        const next = [...prev];
        next[blankIdx] = { ...next[blankIdx], ...lineDefaults };
        return next;
      }
      return [...prev, { ...blankLine(), ...lineDefaults }];
    });
    toast.success(`+ ${prod.sku}`);
  };

  /** Handle product picked from the smooth thumbnail picker sheet.
   *  - When picking for a specific line idx: update that line.
   *  - When picking for "new": find a blank line or append a new one. */
  const handlePickerSelect = (patch) => {
    if (pickerFor === "new") {
      setItems((prev) => {
        const blankIdx = prev.findIndex((it) => !it.productInput && !it.product_id);
        if (blankIdx >= 0) {
          const next = [...prev];
          next[blankIdx] = { ...next[blankIdx], ...patch };
          return next;
        }
        return [...prev, { ...blankLine(), ...patch }];
      });
    } else if (typeof pickerFor === "number") {
      updateLine(pickerFor, patch);
    }
    setPickerFor(null);
  };

  const handleVariantChange = (idx, variantId) => {
    const line = items[idx];
    const prod = products.find((p) => p.id === line.product_id);
    const v = prod?.variants?.find((x) => x.id === variantId);
    const patch = { variant_id: variantId };
    if (v && Number(v.selling_price) > 0) patch.unit_price = v.selling_price;
    else if (prod) patch.unit_price = prod.selling_price || 0;
    updateLine(idx, patch);
  };

  // Calculations
  const lineMeta = items.map((it) => {
    const prod = products.find((p) => p.id === it.product_id) || null;
    const variants = prod?.variants || [];
    const hasVariants = variants.length > 0;
    const selectedVariant = hasVariants ? variants.find((v) => v.id === it.variant_id) : null;
    // Read from `stock` (admin inventory count), falling back to legacy `quantity` for un-migrated rows.
    const stockAvail = selectedVariant
      ? (selectedVariant.stock != null ? selectedVariant.stock : selectedVariant.quantity)
      : (prod ? (prod.stock != null ? prod.stock : (prod.quantity || 0)) : 0);
    // The unit attached to `stock` (not `quantity` which is per-piece weight).
    const stockUnit = selectedVariant
      ? (selectedVariant.stock_unit || prod?.unit || "pcs")
      : (prod?.stock_unit || prod?.unit || "pcs");
    const stockCustomUnit = selectedVariant
      ? (selectedVariant.stock_custom_unit || prod?.custom_unit || "")
      : (prod?.stock_custom_unit || prod?.custom_unit || "");
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.unit_price) || 0;
    const subtotal = qty * price;
    const variantNotPicked = hasVariants && !it.variant_id;
    const isCustom = !it.product_id && it.productInput.trim().length > 0;
    const stockBlocked = prod && !isCustom && qty > stockAvail + 1e-9;
    // ---- Cost / margin (admin & accountant only) ----
    const unitCost = selectedVariant?.cost_price != null
      ? Number(selectedVariant.cost_price)
      : Number(prod?.cost_price || 0);
    const lineCost = unitCost * qty;
    const lineMargin = subtotal - lineCost;
    const lineMarginPct = subtotal > 0 ? (lineMargin / subtotal) * 100 : 0;
    const isLossSale = prod && unitCost > 0 && price < unitCost;
    return { prod, hasVariants, variants, selectedVariant, stockAvail, stockUnit, stockCustomUnit, qty, price, subtotal, variantNotPicked, isCustom, stockBlocked, unitCost, lineCost, lineMargin, lineMarginPct, isLossSale };
  });

  const subtotal_amount = lineMeta.reduce((a, m) => a + m.subtotal, 0);
  const afterDiscount = Math.max(0, subtotal_amount - (parseFloat(invoice_discount) || 0));
  const tax_amount = afterDiscount * ((parseFloat(tax_pct) || 0) / 100);
  const grand_total = afterDiscount + tax_amount;

  const selectedSeller = salesUsers.find((s) => s.id === seller_id);
  const effectiveRate = override_rate !== "" ? parseFloat(override_rate) : (selectedSeller?.commission_rate || 0);
  const commissionPreview = seller_id ? grand_total * (effectiveRate / 100) + (parseFloat(bonus) || 0) : 0;

  const anyBlocked = lineMeta.some((m) => m.stockBlocked || m.variantNotPicked);
  const allLinesFilled = items.every((it) => it.productInput.trim().length > 0 && parseFloat(it.quantity) > 0 && parseFloat(it.unit_price) >= 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!customerInput.trim()) return toast.error("Customer required");
    if (isCustomCustomer && !customCustomerPhone.trim()) return toast.error("Mobile number required for new customer");
    if (!allLinesFilled) return toast.error("Fill all line items");
    if (anyBlocked) return toast.error("Fix line errors (variant or stock)");
    setSubmitting(true);
    try {
      const payload = {
        customer_id,
        custom_customer_name: isCustomCustomer ? customerInput.trim() : "",
        custom_customer_phone: isCustomCustomer ? customCustomerPhone.trim() : "",
        custom_customer_address: isCustomCustomer ? customCustomerAddress.trim() : "",
        items: items.map((it, i) => {
          const meta = lineMeta[i];
          return {
            product_id: it.product_id || "",
            variant_id: it.variant_id || "",
            custom_product_name: meta.isCustom ? it.productInput.trim() : "",
            quantity: parseFloat(it.quantity),
            unit_price: parseFloat(it.unit_price),
          };
        }),
        invoice_discount: parseFloat(invoice_discount) || 0,
        tax_pct: parseFloat(tax_pct) || 0,
        payment_method,
        notes,
      };
      // Partial-payment opt-in: empty = full payment (backward compat)
      if (amount_received !== "" && parseFloat(amount_received) >= 0) {
        payload.initial_payment_amount = parseFloat(amount_received);
        payload.initial_payment_type = deposit_type;
      }
      if (seller_id) {
        payload.seller_id = seller_id;
        if (override_rate !== "") payload.commission_rate_override = parseFloat(override_rate);
        if (bonus !== "") payload.commission_bonus = parseFloat(bonus);
      }
      if (isAdmin && sold_by_user_id) payload.sold_by_user_id = sold_by_user_id;
      await api.post("/accounting/sales/multi", payload);
      toast.success(`Invoice saved · ${items.length} item${items.length === 1 ? "" : "s"} · ${formatCurrency(grand_total)}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Record Sale" subtitle="New invoice · POS mode" onClose={onClose} wide>
      <form onSubmit={submit} className="px-7 py-6 space-y-5" data-testid="sale-form">
        {/* Customer & Seller */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Customer (type to search or add new)">
            <input
              required
              list="gv-sale-customers"
              value={customerInput}
              onChange={(e) => setCustomerInput(e.target.value)}
              placeholder="Search by name — or type a new customer name to auto-create"
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="sale-customer"
            />
            <datalist id="gv-sale-customers">
              {customers.map((c) => (
                <option key={c.id} value={c.name} label={c.company || c.email || ""} />
              ))}
            </datalist>
            {isCustomCustomer && (
              <div className="mt-1.5 text-[0.65rem] mono text-[#7BA585]" data-testid="custom-customer-hint">
                + New customer "{customerInput.trim()}" will be auto-created
              </div>
            )}
          </Field>
          <Field label="Sales User (optional — leave blank for walk-in)">
            <select value={seller_id} onChange={(e) => setSellerId(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="sale-seller">
              <option value="">— Walk-in / no commission —</option>
              {salesUsers.map((s) => (
                <option key={s.id} value={s.id}>{s.name} · default {s.commission_rate?.toFixed(1)}%</option>
              ))}
            </select>
          </Field>
        </div>

        {isAdmin && (
          <Field label="Sold By (which staff this sale came from — tracking only, no commission)">
            <select value={sold_by_user_id} onChange={(e) => setSoldBy(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="sale-sold-by">
              <option value="">— Walk-in customer (default) —</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>{m.name} · {m.role}</option>
              ))}
            </select>
          </Field>
        )}

        {isCustomCustomer && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-[#7BA585]/15 p-4 bg-[#7BA585]/[0.025]" data-testid="custom-customer-fields">
            <Field label="Mobile Number (required)">
              <input required type="tel" placeholder="+62 ..." value={customCustomerPhone} onChange={(e) => setCustomCustomerPhone(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm mono" data-testid="custom-customer-phone" />
            </Field>
            <Field label="Address (optional)">
              <input value={customCustomerAddress} onChange={(e) => setCustomCustomerAddress(e.target.value)} placeholder="Jakarta / Bangkok / ..." className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="custom-customer-address" />
            </Field>
          </div>
        )}

        {/* Line Items Table */}
        <div className="border border-[#D4AF37]/15 bg-[#D4AF37]/[0.02]">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between flex-wrap gap-2">
            <div className="overline text-[#D4AF37]">Line Items ({items.length})</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="btn-gold px-3 py-1.5 text-[0.65rem] uppercase tracking-wider inline-flex items-center gap-1.5"
                data-testid="open-scanner"
                title="POS scanner — works with phone camera and USB/Bluetooth QR readers"
              >
                <ScanLine className="w-3.5 h-3.5" strokeWidth={1.5} />
                Scan QR
              </button>
              <button type="button" onClick={() => setPickerFor("new")} className="btn-outline-gold px-3 py-1.5 text-[0.65rem] uppercase tracking-wider" data-testid="add-line-item">
                + Add product
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="line-items-table">
              <thead>
                <tr className="text-left overline text-[0.55rem] text-[#7a7a7a] border-b border-white/5">
                  <th className="px-3 py-2 font-normal w-[28%]">Product</th>
                  <th className="px-3 py-2 font-normal w-[18%]">Variant</th>
                  <th className="px-3 py-2 font-normal w-[10%]">Qty</th>
                  <th className="px-3 py-2 font-normal w-[8%]">Unit</th>
                  <th className="px-3 py-2 font-normal w-[14%]">Unit Price</th>
                  <th className="px-3 py-2 font-normal text-right w-[14%]">Subtotal</th>
                  <th className="px-3 py-2 w-[8%]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const meta = lineMeta[idx];
                  return (
                    <tr key={it.key} className="border-b border-white/5 align-top" data-testid={`line-row-${idx}`}>
                      <td className="px-3 py-2">
                        {meta.prod ? (
                          /* Picked existing product — show thumbnail + name + SKU + Change */
                          <div className="flex items-start gap-2" data-testid={`line-picked-${idx}`}>
                            <button
                              type="button"
                              onClick={() => setPickerFor(idx)}
                              className="w-11 h-11 shrink-0 border border-white/10 bg-white/[0.03] flex items-center justify-center overflow-hidden hover:border-[#D4AF37]/40 transition-colors"
                              title="Change product"
                              data-testid={`line-thumb-${idx}`}
                            >
                              {Array.isArray(meta.prod.photos) && meta.prod.photos[0] ? (
                                <img
                                  src={meta.prod.photos[0]}
                                  alt={meta.prod.name}
                                  loading="lazy"
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              ) : (
                                <Gem className="w-4 h-4 text-[#444]" strokeWidth={1.5} />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-[#EAEAEA] truncate" title={meta.prod.name}>{meta.prod.name}</div>
                              <div className="text-[0.6rem] mono text-[#666] mt-0.5 truncate">{meta.prod.sku}</div>
                              <button
                                type="button"
                                onClick={() => setPickerFor(idx)}
                                className="text-[0.6rem] mono text-[#C99356] hover:text-[#D4AF37] mt-0.5"
                                data-testid={`line-change-${idx}`}
                              >
                                Change
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* Empty line — tap to open picker. Shows typed custom name if present. */
                          <button
                            type="button"
                            onClick={() => setPickerFor(idx)}
                            className="w-full text-left luxe-input px-2 py-2 text-xs hover:border-[#D4AF37]/40 transition-colors flex items-center gap-2"
                            data-testid={`line-product-${idx}`}
                          >
                            <Search className="w-3.5 h-3.5 text-[#666] shrink-0" strokeWidth={1.5} />
                            {it.productInput ? (
                              <span className="text-[#EAEAEA] truncate">{it.productInput}</span>
                            ) : (
                              <span className="text-[#666]">Pick product…</span>
                            )}
                          </button>
                        )}
                        {meta.isCustom && (
                          <div className="mt-1 text-[0.6rem] mono text-[#7BA585]" data-testid={`line-custom-hint-${idx}`}>+ auto-create</div>
                        )}
                        {meta.prod && (
                          <div className="mt-1 text-[0.6rem] mono text-[#666]">
                            {({ONLINE_SALE:"Online",SHOWROOM:"Showroom",LIVE_PRODUCT:"Live"}[meta.prod.warehouse] || meta.prod.warehouse)} · {formatQty(meta.stockAvail, meta.stockUnit, meta.stockCustomUnit)} avail
                          </div>
                        )}
                        {showMargin && meta.prod && meta.qty > 0 && (
                          <div
                            className={`mt-1 text-[0.6rem] mono ${meta.isLossSale ? "text-red-400 font-semibold" : meta.lineMargin >= 0 ? "text-[#7BA585]" : "text-[#C99356]"}`}
                            data-testid={`line-margin-${idx}`}
                          >
                            {meta.isLossSale && "⚠ "}
                            Cost {formatCurrency(meta.unitCost)} · Margin {formatCurrency(meta.lineMargin)} ({meta.lineMarginPct.toFixed(1)}%)
                            {meta.isLossSale && " — selling below cost"}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {meta.hasVariants ? (
                          <select
                            required
                            value={it.variant_id}
                            onChange={(e) => handleVariantChange(idx, e.target.value)}
                            className="luxe-input w-full px-2 py-2 text-xs"
                            data-testid={`line-variant-${idx}`}
                          >
                            <option value="">— pick —</option>
                            {meta.variants.map((v) => {
                              const label = [v.color, v.size_mm ? `${v.size_mm}mm` : v.size_label].filter(Boolean).join(" · ");
                              const priceTxt = Number(v.selling_price) > 0
                                ? ` · Rp ${Number(v.selling_price).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`
                                : "";
                              // Variant stock with its own unit, fallback for legacy un-migrated variants.
                              const vStock = v.stock != null ? v.stock : v.quantity;
                              const vUnit = v.stock_unit || meta.prod?.unit || "pcs";
                              const vCustom = v.stock_custom_unit || meta.prod?.custom_unit || "";
                              return (
                                <option key={v.id} value={v.id} disabled={vStock <= 0}>
                                  {label}{priceTxt} — {formatQty(vStock, vUnit, vCustom)} avail.
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          <span className="text-[#666] text-[0.65rem]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number" step="0.01" min="0.01"
                          value={it.quantity}
                          onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                          className="luxe-input w-full px-2 py-2 text-xs mono"
                          data-testid={`line-qty-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-[0.65rem] text-[#8A8A8A] mono pt-4">
                        {meta.prod ? unitLabel(meta.prod.unit, meta.prod.custom_unit) : "pcs"}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number" step="1" min="0"
                          value={it.unit_price}
                          onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                          className="luxe-input w-full px-2 py-2 text-xs mono"
                          data-testid={`line-price-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right mono text-[#D4AF37]" data-testid={`line-subtotal-${idx}`}>
                        {formatCurrency(meta.subtotal)}
                      </td>
                      <td className="px-3 py-2 text-center align-middle">
                        <button
                          type="button"
                          onClick={() => {
                            if (items.length > 1) {
                              removeLine(idx);
                            } else {
                              // Last line — reset to blank instead of removing
                              setItems([blankLine()]);
                            }
                          }}
                          className="inline-flex items-center justify-center w-8 h-8 border border-red-400/30 bg-red-400/5 text-red-400 hover:bg-red-400/20 hover:border-red-400/60 transition-colors"
                          title="Remove this item"
                          data-testid={`line-remove-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {anyBlocked && (
            <div className="px-4 py-2 text-[0.65rem] text-red-400 border-t border-red-400/20 bg-red-400/[0.05]" data-testid="line-error">
              Fix line errors: pick a variant for variant-products and ensure quantity ≤ stock available.
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Field label="Payment Method">
              <select value={payment_method} onChange={(e) => setPay(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm">
                {["Cash", "Wire", "Card", "Crypto"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Notes">
              <input value={notes} onChange={(e) => setNotes(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" />
            </Field>
            {seller_id && (
              <div className="grid grid-cols-2 gap-2 border border-[#D4AF37]/15 p-3 bg-[#D4AF37]/[0.025]">
                <Field label="Override Rate (%)">
                  <input type="number" step="0.1" min="0" max="100" value={override_rate} onChange={(e) => setOverrideRate(e.target.value)} className="luxe-input w-full px-3 py-2 text-xs" placeholder={`${selectedSeller?.commission_rate ?? 0}`} data-testid="sale-override-rate" />
                </Field>
                <Field label="Bonus (IDR)">
                  <input type="number" step="1" value={bonus} onChange={(e) => setBonus(e.target.value)} className="luxe-input w-full px-3 py-2 text-xs" data-testid="sale-bonus" />
                </Field>
                <div className="col-span-2 text-[0.65rem] flex justify-between border-t border-white/5 pt-2 mt-1" data-testid="commission-preview">
                  <span className="text-[#8A8A8A]">Commission</span>
                  <span className="mono text-[#D4AF37]">
                    {effectiveRate.toFixed(1)}% of {formatCurrency(grand_total)} = <strong>{formatCurrency(commissionPreview)}</strong>
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-5 space-y-3" data-testid="invoice-summary">
            <div className="flex justify-between text-xs">
              <span className="text-[#8A8A8A]">Total Items</span>
              <span className="mono">{items.length}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-[#8A8A8A]">Subtotal</span>
              <span className="mono" data-testid="summary-subtotal">{formatCurrency(subtotal_amount)}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-[#8A8A8A]">Discount (IDR)</span>
              <input type="number" step="1" min="0" value={invoice_discount} onChange={(e) => setInvoiceDiscount(e.target.value)} className="luxe-input w-32 px-2 py-1.5 text-xs mono text-right" data-testid="summary-discount" />
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-[#8A8A8A]">Tax (%)</span>
              <input type="number" step="0.1" min="0" max="100" value={tax_pct} onChange={(e) => setTaxPct(e.target.value)} className="luxe-input w-32 px-2 py-1.5 text-xs mono text-right" placeholder="0" data-testid="summary-tax-pct" />
            </div>
            {parseFloat(tax_pct) > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-[#8A8A8A]">Tax amount</span>
                <span className="mono" data-testid="summary-tax-amount">{formatCurrency(tax_amount)}</span>
              </div>
            )}
            <div className="border-t border-[#D4AF37]/30 pt-3 flex justify-between items-baseline">
              <span className="overline text-[#D4AF37]">Grand Total</span>
              <span className="serif text-2xl text-[#D4AF37]" data-testid="summary-grand-total">{formatCurrency(grand_total)}</span>
            </div>

            {showMargin && (() => {
              const totalCost = lineMeta.reduce((a, m) => a + m.lineCost, 0);
              const grossProfit = afterDiscount - totalCost;
              const marginPct = afterDiscount > 0 ? (grossProfit / afterDiscount) * 100 : 0;
              const lossLines = lineMeta.filter((m) => m.isLossSale).length;
              return (
                <div className="border-t border-white/5 pt-2 space-y-1" data-testid="invoice-margin-summary">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#8A8A8A]">COGS (total cost)</span>
                    <span className="mono text-[#8A8A8A]">{formatCurrency(totalCost)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className={grossProfit < 0 ? "text-red-400" : "text-[#7BA585]"}>
                      Gross profit · {marginPct.toFixed(1)}%
                    </span>
                    <span className={`mono ${grossProfit < 0 ? "text-red-400" : "text-[#7BA585]"}`}>
                      {formatCurrency(grossProfit)}
                    </span>
                  </div>
                  {lossLines > 0 && (
                    <div className="text-[0.65rem] text-red-400 mono">
                      ⚠ {lossLines} line{lossLines === 1 ? "" : "s"} selling below cost
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Partial-payment: Deposit / Amount Received */}
            <div className="border-t border-white/5 pt-3 space-y-2" data-testid="partial-payment-block">
              <div className="overline text-[#7BA585] text-[0.6rem]">Payment received now (leave blank = paid in full)</div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="1"
                  min="0"
                  max={grand_total}
                  placeholder={`Default: ${formatCurrency(grand_total)}`}
                  value={amount_received}
                  onChange={(e) => setAmountReceived(e.target.value)}
                  className="luxe-input px-3 py-2 text-xs mono"
                  data-testid="sale-amount-received"
                />
                <select
                  value={deposit_type}
                  onChange={(e) => setDepositType(e.target.value)}
                  className="luxe-input px-3 py-2 text-xs"
                  data-testid="sale-deposit-type"
                >
                  <option value="Deposit">Deposit</option>
                  <option value="Installment">Installment</option>
                  <option value="Balance">Balance</option>
                </select>
              </div>
              {amount_received !== "" && parseFloat(amount_received) < grand_total - 0.001 && (
                <div className="flex justify-between text-[0.65rem] mono pt-1" data-testid="partial-preview">
                  <span className="text-[#C99356]">Balance due after this payment</span>
                  <span className="text-[#C99356]">{formatCurrency(grand_total - (parseFloat(amount_received) || 0))}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button
            type="submit"
            data-testid="submit-sale"
            className="btn-gold px-5 py-2.5"
            disabled={submitting || anyBlocked || !allLinesFilled}
          >
            {submitting ? "Saving…" : `Record Invoice · ${formatCurrency(grand_total)}`}
          </button>
        </div>
      </form>
      {/* POS scanner modal (renders above SaleModal) */}
      {showScanner && (
        <QrScannerModal
          title="Scan QR to add"
          onClose={() => setShowScanner(false)}
          onScan={handleScannedProduct}
        />
      )}
      {/* Smooth thumbnail product picker (POS-style) */}
      {pickerFor !== null && (
        <ProductPickerSheet
          products={products}
          onPick={handlePickerSelect}
          onClose={() => setPickerFor(null)}
        />
      )}
    </Modal>
  );
}

function ExpenseModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ category: "Office Rent", amount: 0, description: "", date: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounting/expenses", {
        ...form,
        amount: Number(form.amount),
        date: form.date ? new Date(form.date).toISOString() : null,
      });
      toast.success("Expense added");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Modal title="Add Expense" subtitle="New entry" onClose={onClose}>
      <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="expense-form">
        <Field label="Category">
          <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
        </Field>
        <Field label="Amount (IDR)">
          <input required type="number" step="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
        </Field>
        <Field label="Description">
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
        </Field>
        <Field label="Date">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button type="submit" data-testid="submit-expense" className="btn-gold px-5 py-2.5">Add</button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, subtitle, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className={`relative bg-[#0F0F0F] border border-white/10 w-full ${wide ? "max-w-5xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto fade-up`}>
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">{subtitle}</div>
            <h3 className="serif text-2xl mt-1">{title}</h3>
          </div>
          <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      {children}
    </div>
  );
}

// ---------- PAYMENT STATUS BADGE ----------
function PaymentStatusBadge({ sale }) {
  const status = sale.payment_status || "Paid";
  const balance = sale.balance_due || 0;
  const styles = {
    "Paid": "border-[#7BA585]/40 text-[#7BA585] bg-[#7BA585]/[0.07]",
    "Partially Paid": "border-[#C99356]/40 text-[#C99356] bg-[#C99356]/[0.07]",
    "Unpaid": "border-red-500/40 text-red-400 bg-red-500/[0.07]",
  };
  return (
    <div
      className={`inline-flex flex-col items-end gap-0.5 px-2 py-1 border text-[0.6rem] uppercase tracking-wider ${styles[status] || styles["Paid"]}`}
      data-testid={`payment-status-${sale.invoice_number}`}
    >
      <span>{status}</span>
      {status !== "Paid" && balance > 0 && (
        <span className="mono text-[0.55rem] opacity-90">−{formatCurrency(balance)}</span>
      )}
    </div>
  );
}

// ---------- PAYMENT LEDGER MODAL ----------
function PaymentLedgerModal({ sale, isAdmin, onClose, onChanged, onFullPaid }) {
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("Installment");
  const [method, setMethod] = useState("Wire");
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const balance = sale.balance_due || 0;
  const paid = sale.paid_total || 0;
  const total = sale.total || 0;
  const isPaid = sale.payment_status === "Paid";

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Amount must be positive");
    if (amt > balance + 0.01) return toast.error(`Amount cannot exceed balance ${formatCurrency(balance)}`);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/accounting/sales/${sale.id}/payments`, {
        amount: amt,
        type,
        method,
        note: note.trim(),
        date: date ? new Date(date).toISOString() : null,
      });
      toast.success(`Payment recorded · balance now ${formatCurrency(data.balance_due)}`);
      onChanged(data);
      if (data.payment_status === "Paid") onFullPaid();
      setAmount("");
      setNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  const deletePayment = async (pid) => {
    const isLocked = sale.signed || sale.locked;
    const msg = isLocked
      ? `⚠ This invoice is signed${sale.emeterai_applied ? " + e-METERAI STAMPED" : ""}.\n\nRemoving a payment from a locked invoice will flag the doc as 'force-edited'. Audit-logged.\n\nContinue?`
      : "Permanently remove this payment? If the sale was already fully Paid, the commission row will also be reversed.";
    if (!window.confirm(msg)) return;
    try {
      const qs = isLocked ? "?force=true" : "";
      const { data } = await api.delete(`/accounting/sales/${sale.id}/payments/${pid}${qs}`);
      toast.success(isLocked ? "Payment removed (force override)" : "Payment removed");
      onChanged(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <Modal title={`Payments · ${sale.invoice_number}`} subtitle={sale.customer_name} onClose={onClose} wide>
      <div className="px-7 py-6 space-y-6" data-testid="payment-ledger">
        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-4 border border-[#D4AF37]/20 bg-[#0A0A0A] p-5" data-testid="ledger-summary">
          <div>
            <div className="overline text-[0.55rem] text-[#7a7a7a]">Invoice Total</div>
            <div className="serif text-xl mt-1">{formatCurrency(total)}</div>
          </div>
          <div>
            <div className="overline text-[0.55rem] text-[#7a7a7a]">Paid to date</div>
            <div className="serif text-xl mt-1 text-[#7BA585]" data-testid="ledger-paid">{formatCurrency(paid)}</div>
          </div>
          <div>
            <div className="overline text-[0.55rem] text-[#7a7a7a]">Balance Due</div>
            <div className={`serif text-xl mt-1 ${isPaid ? "text-[#7BA585]" : "text-[#C99356]"}`} data-testid="ledger-balance">
              {formatCurrency(balance)}
            </div>
          </div>
          <div className="col-span-3 pt-2 border-t border-white/5 flex items-center justify-between">
            <PaymentStatusBadge sale={sale} />
            <div className="text-[0.65rem] text-[#7a7a7a] mono">{(sale.payments || []).length} payment(s) recorded</div>
          </div>
        </div>

        {/* Add payment form */}
        {!isPaid && (
          <form onSubmit={submit} className="border border-[#7BA585]/20 bg-[#7BA585]/[0.025] p-5 space-y-3" data-testid="add-payment-form">
            <div className="overline text-[#7BA585] text-[0.6rem]">Record New Payment</div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <Field label="Amount (IDR)">
                <input
                  required type="number" step="1" min="1" max={balance}
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="luxe-input w-full px-3 py-2 text-sm mono"
                  data-testid="add-payment-amount"
                  placeholder={formatCurrency(balance)}
                />
              </Field>
              <Field label="Type">
                <select value={type} onChange={(e) => setType(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm" data-testid="add-payment-type">
                  <option value="Deposit">Deposit</option>
                  <option value="Installment">Installment</option>
                  <option value="Balance">Balance</option>
                </select>
              </Field>
              <Field label="Method">
                <select value={method} onChange={(e) => setMethod(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm" data-testid="add-payment-method">
                  {["Cash", "Wire", "Card", "Crypto"].map((p) => <option key={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Date (optional)">
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm" data-testid="add-payment-date" />
              </Field>
              <Field label="Note (optional)">
                <input value={note} onChange={(e) => setNote(e.target.value)} className="luxe-input w-full px-3 py-2 text-sm" data-testid="add-payment-note" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="submit" disabled={submitting} className="btn-gold px-5 py-2" data-testid="submit-payment">
                {submitting ? "Saving…" : "Record Payment"}
              </button>
            </div>
          </form>
        )}

        {/* Payment history */}
        <div className="border border-white/5 bg-[#0A0A0A]/40 p-5" data-testid="payment-history">
          <div className="overline text-[#D4AF37] text-[0.6rem] mb-3">Payment History</div>
          {(sale.payments || []).length === 0 ? (
            <div className="text-xs text-[#666] italic">No payments recorded.</div>
          ) : (
            <div className="space-y-2">
              {(sale.payments || []).map((p, i) => (
                <div key={p.id} className="grid grid-cols-12 gap-2 items-center text-xs py-2 border-b border-white/5 last:border-0" data-testid={`payment-row-${i}`}>
                  <div className="col-span-2 mono text-[#8A8A8A]">{(p.date || "").slice(0, 10)}</div>
                  <div className="col-span-2 text-[#D4AF37]">{p.type}</div>
                  <div className="col-span-2 text-[#8A8A8A]">{p.method}</div>
                  <div className="col-span-3 mono text-right">{formatCurrency(p.amount)}</div>
                  <div className="col-span-2 text-[0.65rem] text-[#666] truncate" title={p.note || ""}>{p.note || "—"}</div>
                  <div className="col-span-1 text-right">
                    {isAdmin && (
                      <button
                        onClick={() => deletePayment(p.id)}
                        className="text-[#8A8A8A] hover:text-red-400 p-1"
                        data-testid={`delete-payment-${i}`}
                        title="Remove (admin only)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-outline-gold px-5 py-2.5" data-testid="close-ledger">Close</button>
        </div>
      </div>
    </Modal>
  );
}


// =====================================================================
// RETURN SALE MODAL
// =====================================================================
function ReturnSaleModal({ sale, onClose, onSaved }) {
  // Flatten sale into item rows we can toggle a return-qty on.
  const sourceLines = sale.is_multi && sale.items
    ? sale.items
    : [{
        product_id: sale.product_id,
        variant_id: sale.variant_id,
        product_name: sale.product_name,
        product_sku: sale.product_sku,
        quantity: sale.quantity,
        unit_price: sale.unit_price,
      }];

  const [rows, setRows] = useState(() =>
    sourceLines.map((it) => ({
      product_id: it.product_id,
      variant_id: it.variant_id || null,
      product_name: it.product_name,
      product_sku: it.product_sku,
      max_qty: Number(it.quantity || 0),
      qty: "",   // STRING so users can clear it (avoid the leading-0 bug)
      reason: "",
      unit_price: Number(it.unit_price || 0),
    }))
  );
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMethod, setRefundMethod] = useState("Cash");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Auto-suggest refund = sum of (qty × unit_price) whenever the user
  // hasn't manually typed a refund amount yet.
  const [manualRefund, setManualRefund] = useState(false);
  const suggested = rows.reduce((sum, r) => sum + (Number(r.qty) || 0) * r.unit_price, 0);
  const effectiveRefund = manualRefund ? Number(refundAmount) || 0 : suggested;

  const submit = async () => {
    const items = rows
      .filter((r) => Number(r.qty) > 0)
      .map((r) => ({
        product_id: r.product_id,
        variant_id: r.variant_id || null,
        quantity: Number(r.qty),
        reason: r.reason,
      }));
    if (!items.length) return toast.error("Enter a return quantity on at least one item");
    setBusy(true);
    try {
      await api.post(`/accounting/sales/${sale.id}/returns`, {
        items,
        refund_amount: effectiveRefund,
        refund_method: effectiveRefund > 0 ? refundMethod : "None",
        reason,
      });
      toast.success(`Return processed — stock restored${effectiveRefund > 0 ? ` · Rp ${effectiveRefund.toLocaleString("id-ID")} refunded` : ""}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Return failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={`Return Items · ${sale.invoice_number}`}>
      <div className="space-y-4">
        <div className="text-xs text-[#8A8A8A]">
          Original sale is kept in history. Returned qty is added back to stock;
          an optional refund is recorded as a negative payment on this invoice.
        </div>

        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="bg-black/20 border border-white/5 p-3 grid grid-cols-12 gap-2 items-center"
                 data-testid={`return-item-${i}`}>
              <div className="col-span-12 md:col-span-5">
                <div className="mono text-[#D4AF37] text-xs">{r.product_sku}</div>
                <div className="text-sm">{r.product_name}</div>
                <div className="text-[0.6rem] mono text-[#666]">sold: {r.max_qty} @ Rp {r.unit_price.toLocaleString("id-ID")}</div>
              </div>
              <div className="col-span-6 md:col-span-3">
                <div className="overline text-[0.5rem] mb-0.5">Qty to return</div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={r.qty}
                  onChange={(e) => {
                    const clean = e.target.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
                    const capped = Math.min(Number(clean) || 0, r.max_qty);
                    update(i, { qty: capped ? String(capped) : "" });
                    setManualRefund(false);
                  }}
                  className="luxe-input w-full px-2 py-1.5 text-sm mono"
                  placeholder={`0 – ${r.max_qty}`}
                  data-testid={`return-qty-${i}`}
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="overline text-[0.5rem] mb-0.5">Reason</div>
                <input value={r.reason}
                       onChange={(e) => update(i, { reason: e.target.value })}
                       className="luxe-input w-full px-2 py-1.5 text-xs"
                       placeholder="e.g. wrong size" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-12 gap-3 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 p-3">
          <div className="col-span-6 md:col-span-4">
            <div className="overline text-[0.55rem] mb-1">Refund amount (IDR)</div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={manualRefund ? refundAmount : String(suggested || "")}
              onChange={(e) => {
                setManualRefund(true);
                const clean = e.target.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
                setRefundAmount(clean);
              }}
              className="luxe-input w-full px-3 py-2 mono"
              placeholder="0 = no refund"
              data-testid="return-refund-amount"
            />
            <div className="text-[0.6rem] text-[#666] mono mt-0.5">
              Suggested: Rp {suggested.toLocaleString("id-ID")}
            </div>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="overline text-[0.55rem] mb-1">Refund method</div>
            <select value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)}
                    className="luxe-input w-full px-3 py-2"
                    data-testid="return-refund-method">
              <option>Cash</option><option>Wire</option><option>Card</option><option>Crypto</option>
            </select>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-[0.55rem] mb-1">Note (optional)</div>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
                   className="luxe-input w-full px-3 py-2 text-sm"
                   placeholder="e.g. Customer size mismatch" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="btn-gold px-6 py-2.5 disabled:opacity-50"
                  data-testid="submit-return">
            {busy ? "Processing…" : "Process Return"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =====================================================================
// EDIT SALE MODAL — customer info + custom date
// =====================================================================
function EditSaleModal({ sale, onClose, onSaved }) {
  const [name, setName] = useState(sale.customer_name || "");
  const [phone, setPhone] = useState(sale.customer_phone || "");
  const [notes, setNotes] = useState(sale.notes || "");
  // ISO date part only (YYYY-MM-DD) so the native date picker works.
  const [date, setDate] = useState((sale.created_at || "").slice(0, 10));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      // Preserve the existing time-of-day on the transaction date — we
      // only let the admin pick a calendar date, not a full timestamp.
      const originalTime = (sale.created_at || "").slice(10) || "T00:00:00+00:00";
      const patchedDate = date ? `${date}${originalTime}` : sale.created_at;
      await api.patch(`/accounting/sales/${sale.id}`, {
        customer_name: name,
        customer_phone: phone,
        created_at: patchedDate,
        notes,
      });
      toast.success("Sale updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={`Edit ${sale.invoice_number}`}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="overline text-[0.55rem] mb-1">Customer name</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   className="luxe-input w-full px-3 py-2"
                   data-testid="edit-customer-name" />
          </label>
          <label className="block">
            <div className="overline text-[0.55rem] mb-1">Phone</div>
            <input value={phone} onChange={(e) => setPhone(e.target.value)}
                   className="luxe-input w-full px-3 py-2 mono"
                   placeholder="+62 …"
                   data-testid="edit-customer-phone" />
          </label>
          <label className="block">
            <div className="overline text-[0.55rem] mb-1">Transaction date</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="luxe-input w-full px-3 py-2 mono"
                   data-testid="edit-custom-date" />
            <div className="text-[0.6rem] text-[#666] mono mt-0.5">
              Original: {(sale.created_at || "").slice(0, 10)}
            </div>
          </label>
          <label className="block md:col-span-2">
            <div className="overline text-[0.55rem] mb-1">Notes</div>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="luxe-input w-full px-3 py-2" />
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="btn-gold px-6 py-2.5 disabled:opacity-50"
                  data-testid="submit-edit-sale">
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
